"use client";

import { IFile } from "@/utils/engines/queue";
import { formatSize, getCompressRatio } from "@/utils/engines/utils";
import { cn } from "@/utils/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { X } from "lucide-react";

export interface CompareState {
  x: number;
  xrate: number;
  scale: number;
  moving: boolean;
  status: "show" | "hide";
  dividerWidth: number;
  imageWidth: number;
  imageHeight: number;
  containerWidth: number;
  containerHeight: number;
}

const Compare = (props: { preview: IFile; onClose: () => void }) => {
  const infoRef = useRef<Required<IFile>>(props.preview as Required<IFile>);
  const containerRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<CompareState>({
    x: 0,
    xrate: 0.5,
    scale: 0.8,
    moving: false,
    status: "show",
    dividerWidth: 2,
    containerWidth: 0,
    containerHeight: 0,
    imageWidth: 0,
    imageHeight: 0
  });
  const [oldLoaded, setOldLoaded] = useState<boolean>(false);
  const [newLoaded, setNewLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  const update = useCallback(
    (newState: Partial<CompareState>) => {
      setState({
        ...state,
        ...newState
      });
    },
    [state]
  );

  const getState = useCallback(() => {
    return state;
  }, [state]);

  const updateRef = useRef<(newState: Partial<CompareState>) => void>(update);
  const stateRef = useRef<() => CompareState>(getState);
  useEffect(() => {
    updateRef.current = update;
    stateRef.current = getState;
  }, [update, getState]);

  useEffect(() => {
    if (oldLoaded && newLoaded) {
      setLoading(false);
    }
  }, [oldLoaded, newLoaded]);

  useEffect(() => {
    infoRef.current = props.preview as Required<IFile>;
  }, [props.preview]);

  useEffect(() => {
    const doc = document.documentElement;
    const bar = barRef.current!;

    let isControl = false;
    let cursorX = 0;

    const resize = () => {
      const states = stateRef.current();
      const rect = containerRef.current!.getBoundingClientRect();
      let imageWidth: number;
      let imageHeight: number;
      if (
        infoRef.current.width / infoRef.current.height >
        rect.width / rect.height
      ) {
        imageWidth = rect.width * states.scale;
        imageHeight =
          (imageWidth * infoRef.current.height) / infoRef.current.width;
      } else {
        imageHeight = rect.height * states.scale;
        imageWidth =
          (imageHeight * infoRef.current.width) / infoRef.current.height;
      }
      updateRef.current({
        x: rect.width * states.xrate,
        imageWidth,
        imageHeight,
        containerWidth: rect.width,
        containerHeight: rect.height
      });
    };

    const mousedown = (event: MouseEvent) => {
      isControl = true;
      cursorX = event.clientX;
      updateRef.current({ moving: true });
    };

    const mouseup = () => {
      isControl = false;
      cursorX = 0;
      updateRef.current({ moving: false });
    };

    const mousemove = (event: MouseEvent) => {
      if (isControl) {
        const states = stateRef.current();
        let x = states.x + event.clientX - cursorX;
        const min = (states.containerWidth - states.imageWidth) / 2;
        const max = (states.containerWidth + states.imageWidth) / 2;
        if (x < min) {
          x = min;
        }
        if (x > max) {
          x = max;
        }
        cursorX = event.clientX;
        updateRef.current({ x, xrate: x / states.containerWidth });
      }
    };

    const wheel = (event: WheelEvent) => {
      const states = stateRef.current();
      let scale = -0.001 * event.deltaY + states.scale;
      if (scale > 1) {
        scale = 1;
      }
      if (scale < 0.1) {
        scale = 0.1;
      }

      let imageWidth: number;
      let imageHeight: number;
      if (
        infoRef.current.width / infoRef.current.height >
        states.containerWidth / states.containerHeight
      ) {
        imageWidth = states.containerWidth * scale;
        imageHeight =
          (imageWidth * infoRef.current.height) / infoRef.current.width;
      } else {
        imageHeight = states.containerHeight * scale;
        imageWidth =
          (imageHeight * infoRef.current.width) / infoRef.current.height;
      }

      const innerRate =
        (states.x - (states.containerWidth - states.imageWidth) / 2) /
        states.imageWidth;
      const x =
        innerRate * imageWidth + (states.containerWidth - imageWidth) / 2;

      updateRef.current({ scale, imageWidth, imageHeight, x });
    };

    window.addEventListener("resize", resize);
    window.addEventListener("wheel", wheel);
    bar.addEventListener("mousedown", mousedown);
    doc.addEventListener("mousemove", mousemove);
    doc.addEventListener("mouseup", mouseup);

    resize();

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("wheel", wheel);
      bar.removeEventListener("mousedown", mousedown);
      doc.removeEventListener("mousemove", mousemove);
      doc.removeEventListener("mouseup", mouseup);
    };
  }, []);

  const leftStyle: React.CSSProperties = {
    width: `${state.x}px`
  };
  const rightStyle: React.CSSProperties = {
    width: `${state.containerWidth - state.x}px`
  };
  const barStyle: React.CSSProperties = {
    width: `${state.dividerWidth}px`,
    left: `${state.x - state.dividerWidth / 2}px`,
    opacity: state.x === 0 ? 0 : 1
  };
  const imageStyle: React.CSSProperties = {
    opacity: newLoaded && oldLoaded ? 1 : 0
  };
  const leftImageStyle: React.CSSProperties = {
    width: state.imageWidth,
    height: state.imageHeight,
    left: (state.containerWidth - state.imageWidth) / 2 + "px",
    ...imageStyle
  };
  const rightImageStyle: React.CSSProperties = {
    width: state.imageWidth,
    height: state.imageHeight,
    right: (state.containerWidth - state.imageWidth) / 2 + "px",
    ...imageStyle
  };

  let statusClass: string | undefined = undefined;
  if (state.status === "show") {
    statusClass = "preview-show";
  }
  if (state.status === "hide") {
    statusClass = "preview-hide";
  }

  const ratioText = useMemo(() => {
    const ratio = getCompressRatio(
      infoRef.current.fileSize,
      infoRef.current.compressFileSize
    );
    if (Number(ratio) > 0) {
      return "⇩ " + ratio + "%";
    }
    return "⇧ " + ratio + "%";
  }, [infoRef.current]);

  return createPortal(
    <div
      className={cn(
        "w-[100vw] h-[100vh] fixed top-0 left-0 z-50 select-none overflow-hidden preview-card-container",
        state.moving && "cursor-grab",
        statusClass
      )}
      ref={containerRef}
      onAnimationEnd={event => {
        if (event.animationName === "BoxHide") {
          props.onClose?.();
        }
      }}
    >
      <div
        className="absolute left-0 top-0 bottom-0 overflow-hidden border-box preview-image before"
        style={leftStyle}
        data-beer-label={`Before \n ${formatSize(infoRef.current.fileSize)}`}
      >
        <img
          src={infoRef.current?.src}
          style={leftImageStyle}
          className="absolute top-[50%] translate-y-[-50%] max-w-none"
          onLoad={() => {
            setOldLoaded(true);
          }}
        />
      </div>
      <div
        className="absolute right-0 top-0 bottom-0 overflow-hidden border-box preview-image after"
        style={rightStyle}
        data-beer-label-green={ratioText}
        data-beer-label={`After \n ${formatSize(
          infoRef.current.compressFileSize
        )}`}
      >
        <img
          src={infoRef.current.compress.src}
          style={rightImageStyle}
          className="absolute top-[50%] translate-y-[-50%] max-w-none"
          onLoad={() => {
            setNewLoaded(true);
          }}
        />
      </div>
      <div style={barStyle} className="absolute top-0 bottom-0 z-10 bg-black">
        <div
          className="absolute flex items-center justify-center cursor-grab preview-bar-center"
          ref={barRef}
        >
          <div className="beer-handle" />
        </div>
      </div>
      <Button
        className="absolute top-1 right-[1rem] z-20 rounded-full"
        variant="default"
        onClick={() => {
          updateRef.current?.({ status: "hide" });
        }}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>,
    document.body
  );
};
export default Compare;
